<template>
  <v-stepper
    v-model="currentStep"
    class="full-width elevation-0"
    id="PointsPurchase"
  >
    <v-stepper-header class="full-width elevation-0">
      <template v-for="step in steps">
        <v-stepper-step
          :key="`${step.index}-step`"
          :complete="currentStep > step.index"
          :step="step.index"
          :editable="currentStep > step.index"
        >
          {{ step.title }}
        </v-stepper-step>

        <v-divider v-if="step.index < steps.length" :key="step.index" />
      </template>
    </v-stepper-header>

    <v-stepper-items>
      <!-- Select Points -->
      <v-stepper-content key="step-1-content" step="1">
        <v-card flat tile>
          <v-row no-gutters class="mb-6">
            <v-icon class="mr-2" size="24" color="secondary">fas fa-gem</v-icon>
            <div class="body-heading-large">Purchase Points</div>
          </v-row>

          <v-row no-gutters class="mb-3">
            <v-col cols="12" md="12">
              <v-select
                :class="{ 'half-width': !isMobile }"
                :items="pointsOptions"
                label="Points"
                dense
                outlined
                v-model="selectedPointsObj"
                return-object
                :menu-props="{ offsetY: true }"
              />
            </v-col>
          </v-row>

          <v-row no-gutters align="center" class="mb-2">
            <v-icon class="mr-2" size="24" color="secondary">fas fa-gem</v-icon>
            <div class="body-heading-large mr-6">Auto-Reload</div>
            <v-switch
              v-model="isAutoReload"
              @change="onIsAutoReloadChange"
              class="mt-0"
              hide-details
            ></v-switch>
          </v-row>
          <div class="mb-6">
            You can manage your points auto-reload options in auto-reload
            settings later.
          </div>

          <v-row no-gutters class="mb-3">
            <v-col cols="12" md="12" class="mb-2">
              <div class="body-heading-small">When balance is below:</div>
            </v-col>
            <v-col cols="12" md="12">
              <v-select
                :class="{ 'half-width': !isMobile }"
                :items="autoReloadPointsOptions"
                label="Points"
                dense
                outlined
                :disabled="!isAutoReload"
                v-model="autoReloadPointsAmount"
                :menu-props="{ offsetY: true }"
                hide-details
              />
              <div class="red--text">
                *System will auto-reload points when uncompleted quests bring
                total below threshold
              </div>
            </v-col>
          </v-row>

          <v-btn color="primary" text @click="cancel">Cancel</v-btn>
          <v-btn
            color="secondary"
            @click="goToStep(2)"
            :disabled="!selectedPointsObj.amount"
          >
            Next
          </v-btn>
        </v-card>
      </v-stepper-content>

      <!-- Payment Methods -->
      <v-stepper-content key="step-2-content" step="2">
        <v-card :class="{ 'half-width': !isMobile }" flat tile>
          <div class="body-heading-large">Payment Methods</div>
          <v-card-text class="px-0">
            <PaymentMethodForm
              v-if="isPaymentMethodFormShown || forceShowPaymentForm"
              @force-close="closePaymentMethodForm"
            />
            <v-item-group v-else>
              <v-row class="mb-10" no-gutters>
                <v-col
                  v-for="paymentMethodInfo in paymentMethods"
                  :key="paymentMethodInfo.id"
                  cols="12"
                  md="12"
                  class="py-3"
                >
                  <v-item>
                    <v-card outlined>
                      <v-card-text class="py-0">
                        <v-row>
                          <!-- Payment card information -->
                          <v-col
                            cols="12"
                            md="6"
                            class="d-flex align-center justify-center justify-md-start py-0"
                          >
                            <v-radio-group
                              v-model="selectedPaymentMethodObj"
                              :rules="paymentMethodRules"
                              required
                            >
                              <v-radio
                                :key="paymentMethodInfo.id"
                                :value="paymentMethodInfo"
                              >
                                <template v-slot:label>
                                  <PaymentMethodListItem
                                    :paymentMethod="paymentMethodInfo"
                                  />
                                </template>
                              </v-radio>
                            </v-radio-group>
                          </v-col>
                          <!-- Actions -->
                          <v-col
                            cols="12"
                            md="6"
                            class="d-flex align-center justify-center justify-md-end"
                          >
                            <DeletePaymentMethodModal
                              :paymentMethodId="paymentMethodInfo.id"
                            />
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-item>
                </v-col>
                <v-col
                  cols="12"
                  md="12"
                  class="d-flex justify-md-end justify-center pt-3"
                >
                  <v-btn
                    @click="showPaymentMethodForm"
                    text
                    class="add-payment-method-btn"
                  >
                    + Add New Payment Method
                  </v-btn>
                </v-col>
              </v-row>

              <v-btn color="primary" text @click="cancel">Cancel</v-btn>
              <v-btn
                color="secondary"
                @click="updatePaymentInfo"
                :disabled="!isPaymentMethodSectionValid"
              >
                Review Order
              </v-btn>
            </v-item-group>
          </v-card-text>
        </v-card>
      </v-stepper-content>

      <!-- Review Order -->
      <v-stepper-content key="step-3-content" step="3">
        <v-card flat tile>
          <v-row class="mb-7">
            <v-col cols="12" md="6" class="d-flex justify-center">
              <OrderSummaryList
                v-if="
                  selectedPointsObj.amount &&
                  selectedPaymentMethodObj.billing_details
                "
                :key="selectedPointsObj.amount"
                :pointsAmount="selectedPointsObj.amount"
                :isAutoReload="isAutoReload"
                :autoReloadPointsAmount="autoReloadPointsAmount"
                :paymentMethod="selectedPaymentMethodObj"
              />
            </v-col>
            <v-col cols="12" md="6" class="d-flex justify-center">
              <OrderSummaryCard
                v-if="selectedPointsObj.amount"
                :key="finalTotalPrice"
                :pointsAmount="finalPointsAmount"
                :price="finalBasePrice"
                :totalPrice="finalTotalPrice"
                :tax="finalTax"
                class="order-summary-card"
                :class="{ 'full-width': isMobile }"
                :check-promo-code="checkPromoCode"
                :discount-amount="discountAmount"
              />
            </v-col>
            <v-col
              cols="12"
              md="12"
              class="d-flex justify-center justify-md-start"
            >
              <div>
                Purchases are non-refundable. By submitting this form you
                confirm that you agree to our Terms of Service and Privacy
                Policy.
              </div>
            </v-col>
          </v-row>
          <v-btn color="primary" text @click="cancel">Cancel</v-btn>
          <v-btn color="secondary" @click="confirmCardPaymentStripe">
            Purchase Points
          </v-btn>
        </v-card>
      </v-stepper-content>

      <!-- Result  -->
      <v-stepper-content key="step-4-content" step="4">
        <!-- TODO: move this to a seperated component -->
        <v-card v-if="isPointsPurchaseSuccess" class="py-12 px-4" flat tile>
          <v-theme-provider>
            <div class="text-center">
              <v-avatar color="success" size="70">
                <v-icon large color="white">fas fa-check</v-icon>
              </v-avatar>
            </div>
          </v-theme-provider>
          <v-card-title class="justify-center text-center body-heading-large">
            Awesome! Points have been successfully purchased!
          </v-card-title>

          <v-card-text class="d-flex justify-center">
            <v-row>
              <v-col
                cols="12"
                md="6"
                class="d-flex justify-center justify-md-end"
              >
                <v-btn
                  color="secondary"
                  outlined
                  :to="{ name: 'ParentDashboard' }"
                  >Visit Parent Dashboard
                </v-btn>
              </v-col>
              <v-col
                cols="12"
                md="6"
                class="d-flex justify-center justify-md-start"
              >
                <v-btn color="secondary" @click="bugAgain"
                  >Shop for more points
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <v-card v-else class="py-12 px-4" flat>
          <v-theme-provider>
            <div class="text-center">
              <v-avatar color="error" size="70">
                <v-icon color="white" large>fas fa-times</v-icon>
              </v-avatar>
            </div>
          </v-theme-provider>

          <v-card-title class="justify-center body-heading-large text-center">
            Your payment was not successfully processed. Please try again.
          </v-card-title>
          <v-card-text class="d-flex justify-center">
            <v-row>
              <v-col
                cols="12"
                md="6"
                class="d-flex justify-center justify-md-end"
              >
                <v-btn @click="cancel" outlined color="secondary">Cancel</v-btn>
              </v-col>
              <v-col
                cols="12"
                md="6"
                class="d-flex justify-center justify-md-start"
              >
                <v-btn @click="tryAgain" color="secondary">Try again</v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-stepper-content>
    </v-stepper-items>
  </v-stepper>
</template>

<script>
import PaymentMethodForm from '@/components/Payment/PaymentMethodForm';
import PaymentMethodListItem from '@/components/Payment/PaymentMethodListItem';
import OrderSummaryCard from './OrderSummaryCard';
import OrderSummaryList from './OrderSummaryList';
import DeletePaymentMethodModal from '@/components/Payment/DeletePaymentMethodModal';

export default {
  name: 'PointsPurchase',
  data() {
    return {
      currentStep: 1,
      steps: [
        {
          index: 1,
          title: 'Select Points'
        },
        {
          index: 2,
          title: 'Payment Methods'
        },
        {
          index: 3,
          title: 'Review Order'
        },
        {
          index: 4,
          title: 'Compelete'
        }
      ],
      //FIXME: This should be fetched from back-end
      pointsOptions: [
        {
          text: '1,000 points: $10.00 CAD',
          amount: 1000,
          price: 10
        },
        {
          text: '2,000 points: $20.00 CAD',
          amount: 2000,
          price: 20
        },
        {
          text: '3,000 points: $30.00 CAD',
          amount: 3000,
          price: 30
        },
        {
          text: '4,000 points: $40.00 CAD',
          amount: 4000,
          price: 40
        },
        {
          text: '5,250 points: $50.00 CAD (5% bonus points)',
          amount: 5000,
          price: 50
        },
        {
          text: '11,000 points: $100.00 CAD (10% bonus points)',
          amount: 10000,
          price: 100
        }
      ],
      autoReloadPointsOptions: [
        {
          text: '1,000 points',
          value: 1000
        },
        {
          text: '2,000 points',
          value: 2000
        },
        {
          text: '3,000 points',
          value: 3000
        },
        {
          text: '4,000 points',
          value: 4000
        },
        {
          text: '5,250 points',
          value: 5000
        },
        {
          text: '11,000 points',
          value: 10000
        }
      ],
      selectedPointsObj: {},
      isAutoReload: false,
      autoReloadPointsAmount: null,
      selectedPaymentMethodObj: {},
      stripePaymentClientSecret: undefined,
      realPaymentInfoObj: {},

      //Rules
      pointsRules: [v => !!v || 'Please select points'],
      paymentMethodRules: [v => !!v || 'Please select one payment method'],

      //Controls
      isPointsPurchaseSuccess: false,
      forceShowPaymentForm: undefined
    };
  },

  computed: {
    finalBasePrice() {
      if (this.realPaymentInfoObj && this.realPaymentInfoObj.base_price) {
        return this.$converter.centsToDollarsWithoutRounding(
          this.realPaymentInfoObj.base_price
        );
      }
    },
    discountAmount() {
      if (this.realPaymentInfoObj && this.realPaymentInfoObj.discount) {
        return this.$converter.centsToDollarsWithoutRounding(
          this.realPaymentInfoObj.discount
        );
      }
    },
    finalTotalPrice() {
      if (this.realPaymentInfoObj && this.realPaymentInfoObj.total_price) {
        return this.$converter.centsToDollarsWithoutRounding(
          this.realPaymentInfoObj.total_price
        );
      }
    },
    finalPointsAmount() {
      if (
        this.realPaymentInfoObj &&
        this.realPaymentInfoObj.points &&
        this.realPaymentInfoObj.bonus_points
      ) {
        return (
          this.realPaymentInfoObj.points + this.realPaymentInfoObj.bonus_points
        );
      }
    },
    finalTax() {
      if (this.realPaymentInfoObj && this.realPaymentInfoObj.tax_price) {
        return this.$converter.centsToDollarsWithoutRounding(
          this.realPaymentInfoObj.tax_price
        );
      }
    },
    isMobile() {
      return this.$vuetify.breakpoint.mobile;
    },
    user() {
      return this.$store.getters.userInfo;
    },
    paymentMethods() {
      return this.$store.getters.getPaymentMethods();
    },
    isPaymentMethodFormShown() {
      if (!this.$validator.isEmptyArray(this.paymentMethods)) {
        return false;
      }
      return true;
    },
    isPaymentMethodSectionValid() {
      return !this.$validator.isEmpty(this.selectedPaymentMethodObj.card);
    }
  },

  components: {
    OrderSummaryCard,
    OrderSummaryList,
    PaymentMethodForm,
    PaymentMethodListItem,
    DeletePaymentMethodModal
  },

  methods: {
    updatePointsReloadSettings() {
      this.$http
        .put('/parents', {
          reload_settings: {
            is_auto_reload: this.isAutoReload,
            amount: this.selectedPointsObj.amount,
            threshold: this.autoReloadPointsAmount,
            start_date: this.$moment().format('YYYY-MM-DD')
          }
        })
        .then(response => {
          this.isPointsPurchaseSuccess = true;
          this.$store.commit('setLoadingStatus', false);
          this.goToStep(4);
        })
        .catch(error => {
          this.purchasePointsFail(error);
        });
    },

    reset() {
      this.currentStep = 1;
      this.selectedPointsObj = {};
      this.isPointsPurchaseSuccess = false;
      this.isAutoReload = false;
      this.autoReloadPointsAmount = null;
      this.forceShowPaymentForm = undefined;
      this.selectedPaymentMethodObj = {};

      if (this.$refs.paymentMethodForm) {
        this.$refs.paymentMethodForm.resetValidation();
      }
    },

    onIsAutoReloadChange() {
      this.autoReloadPointsAmount = null;
    },

    updatePaymentInfo() {
      this.$store.commit('setLoadingStatus', true);
      if (this.$validator.isEmpty(this.selectedPaymentMethodObj.card)) {
        this.$store.commit('setLoadingStatus', false);
        this.$store.commit('setError', {
          message: 'No payment card selected',
          type: 'error'
        });
      } else {
        this.createCardPayment();
      }
    },

    showPaymentMethodForm() {
      //This is not good
      this.forceShowPaymentForm = true;
      this.selectedPaymentMethodObj = {};
    },

    closePaymentMethodForm() {
      this.forceShowPaymentForm = false;
    },

    updatePointBalance(newBalance) {
      if (!this.$validator.isEmpty(newBalance)) {
        this.$sessionUser._updateUserInfo(
          this.$formatter.extend(this.user, { points: newBalance })
        );
        this.$store.commit('setUserInfo', { points: newBalance });
      }
    },

    purchasePointsSuccess() {
      if (this.isAutoReload) {
        this.updatePointsReloadSettings();
      } else {
        this.isPointsPurchaseSuccess = true;
        this.$store.commit('setLoadingStatus', false);
        this.goToStep(4);
      }
    },

    purchasePointsFail(error) {
      console.error(error);
      this.isPointsPurchaseSuccess = false;
      this.$store.commit('setLoadingStatus', false);
      this.goToStep(4);
    },

    confirmCardPaymentStripeSuccess(result) {
      if (result.paymentIntent.status === 'succeeded') {
        this.purchasePointsSuccess();
      } else {
        this.purchasePointsFail();
      }
    },

    confirmCardPaymentStripe() {
      let data = {
        payment_method: this.selectedPaymentMethodObj.id,
        receipt_email: this.selectedPaymentMethodObj.billing_details.email
      };

      this.$store.commit('setLoadingStatus', true);

      if (this.stripePaymentClientSecret) {
        this.$stripe
          .confirmCardPayment(this.stripePaymentClientSecret, data)
          .then(result => {
            if (result.error) {
              console.error(result.error);
              this.$store.commit('setError', {
                message: result.error.message,
                type: 'error'
              });
              this.$store.commit('setLoadingStatus', false);
            } else {
              this.confirmCardPaymentStripeSuccess(result);
            }
          });
      } else {
        this.$store.commit('setError', {
          message: this.$formatter.getErrorMsg(),
          type: 'error'
        });
      }
    },

    createCardPayment() {
      this.$store.commit('setLoadingStatus', true);

      //Create Stripe PaymentIntent
      this.$http
        .post('/payments', {
          points: this.selectedPointsObj.amount,
          postal: this.selectedPaymentMethodObj.billing_details.address
            .postal_code
        })
        .then(response => {
          if (response.data.client_secret && response.data.payment) {
            this.stripePaymentClientSecret = response.data.client_secret;
            this.realPaymentInfoObj = response.data.payment;
            this.goToStep(3);
            this.$store.commit('setLoadingStatus', false);
          } else {
            this.$store.commit('setError', {
              message: this.$formatter.getErrorMsg(),
              type: 'error'
            });
            this.$store.commit('setLoadingStatus', false);
          }
        })
        .catch(error => {
          console.error(error);
          this.$store.commit('setError', {
            message: this.$formatter.getErrorMsg(error),
            type: 'error'
          });
          this.$store.commit('setLoadingStatus', false);
        });
    },

    updateCardPayment(promoCode) {
      const paymentId = this.realPaymentInfoObj.id;
      //Create Stripe PaymentIntent
      this.$http
        .put(`/payments/${paymentId}`, {
          points: this.selectedPointsObj.amount,
          postal: this.selectedPaymentMethodObj.billing_details.address
            .postal_code,
          promo: promoCode || null
        })
        .then(response => {
          if (response.data.client_secret && response.data.payment) {
            this.stripePaymentClientSecret = response.data.client_secret;
            this.realPaymentInfoObj = response.data.payment;
          } else {
            this.$store.commit('setError', {
              message: this.$formatter.getErrorMsg(),
              type: 'error'
            });
          }
        })
        .catch(error => {
          this.$store.commit('setError', {
            message: this.$formatter.getErrorMsg(error),
            type: 'error'
          });
        });
    },

    checkPromoCode(promoCode) {
      return this.$http
        .get(`/promos/${promoCode}`)
        .then(res => {
          if (res.status === 200) {
            this.updateCardPayment(promoCode);
          } else {
            console.log(res);
          }
        })
        .catch(e => {
          let errorMessage = '';
          try {
            errorMessage = e.response.data.error.base;
          } catch (e) {
            errorMessage = 'Cannot redeem promo code!!';
          }
          throw new Error(errorMessage);
        });
    },

    goToStep(index) {
      if (index <= this.steps.length) {
        this.currentStep = index;
      }
    },

    cancel() {
      this.$router.push({ name: 'ParentDashboard' });
    },

    tryAgain() {
      this.goToStep(1);
    },

    bugAgain() {
      this.reset();
      this.goToStep(1);
    }
  },

  created() {
    this.$store.commit('setLoadingStatus', true);

    this.$http
      .get('/cards')
      .then(response => {
        this.$store.commit('addPaymentMethods', response.data.data);

        this.$store.commit('setLoadingStatus', false);
      })
      .catch(error => {
        console.error(error);
        this.$store.commit('setError', {
          message: this.$formatter.getErrorMsg(error),
          type: 'error'
        });
        this.$store.commit('setLoadingStatus', false);
      });
  }
};
</script>

<style lang="scss" scoped>
.order-summary-card {
  width: 80%;
}
</style>



// WEBPACK FOOTER //
// src/components/Points/Purchase/PointsPurchase.vue